/**
 * /walk — "Grow." Personal-spirituality tab. Top-down: this week's
 * memory verse (with games) → Go Deeper video catalog → Prayer wall.
 * Route stays at /walk so saved PWA shortcuts and existing deep-links
 * keep working; only the tab label and page header changed.
 */
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type Prayer, type SermonNote } from '../lib/api';
import {
  prayersQueryOptions,
  sermonNotesQueryOptions,
  videosQueryOptions,
} from '../lib/queries';
import { MentionTextarea, renderWithMentions } from '../components/MentionTextarea';
import { groupVideosByTag, type VideoRow } from '../lib/video-tags';
import { ScreenHeader } from '../components/ScreenHeader';
import { Section } from '../components/Section';
import { Sheet } from '../components/Sheet';
import { FocusMenu } from '../components/FocusMenu';
import { IconButton } from '../components/IconButton';
import { Button } from '../components/Button';
import { IconPencil, IconPlus } from '../components/icons';
import { MemoryVerseCard } from '../components/MemoryVerseCard';
import { DailyReadingCard } from '../components/DailyReadingCard';
import { Markdown } from '../lib/markdown';

/** The Grow screen's sections, in display order. "All" (the default)
 *  shows every section; picking one focuses the screen on just that. */
const FOCUS_OPTIONS = [
  { key: 'all', label: 'All' },
  { key: 'verse', label: 'Memory Verse' },
  { key: 'reading', label: 'Reading Plan' },
  { key: 'sermon', label: 'Sermon Notes' },
  { key: 'godeeper', label: 'Go Deeper' },
  { key: 'prayer', label: 'Prayer' },
] as const;
type Focus = (typeof FOCUS_OPTIONS)[number]['key'];

export default function WalkPage() {
  const prayerComposerRef = useRef<HTMLFormElement | null>(null);
  // Which section to focus on. 'all' = the full top-down screen.
  const [focus, setFocus] = useState<Focus>('all');
  const shows = (key: Exclude<Focus, 'all'>) => focus === 'all' || focus === key;

  return (
    <>
      <ScreenHeader
        title="Grow"
        subtitle="Memorize. Watch. Pray."
        right={
          <FocusMenu
            value={focus}
            options={FOCUS_OPTIONS}
            onChange={(k) => setFocus(k as Focus)}
            label="Focus section"
          />
        }
      />
      <div className="px-5 pt-4 pb-6 space-y-6">
        {shows('verse') && <MemoryVerseCard />}
        {shows('reading') && <DailyReadingCard />}
        {shows('sermon') && <SermonNotesSection />}
        {shows('godeeper') && <GoDeeperSection />}
        {shows('prayer') && <PrayerSection composerRef={prayerComposerRef} />}
      </div>
    </>
  );
}

/**
 * Sermon Notes — the member's own notes + family-shared notes, newest
 * Sunday first. "＋ Note" and tapping one of your own notes both open
 * the WYSIWYG editor screen; family notes from other members are
 * read-only here.
 */
function SermonNotesSection() {
  const navigate = useNavigate();
  const list = useQuery(sermonNotesQueryOptions());
  const notes = list.data?.notes ?? [];

  return (
    <Section
      title="Sermon Notes"
      subtitle="What you heard on Sunday."
      action={
        <IconButton
          icon={<IconPlus />}
          label="New sermon note"
          onClick={() => navigate('/walk/sermon-notes')}
        />
      }
    >
      {!list.isLoading && notes.length === 0 ? (
        <p className="text-muted text-center py-4 italic">
          No sermon notes yet. Tap the + after Sunday service.
        </p>
      ) : (
        <div className="divide-y divide-line">
          {notes.map((n) => (
            <SermonNoteCard
              key={n.id}
              note={n}
              onEdit={() => navigate(`/walk/sermon-notes/${n.date}`)}
            />
          ))}
        </div>
      )}
    </Section>
  );
}

function SermonNoteCard({ note, onEdit }: { note: SermonNote; onEdit: () => void }) {
  const sunday = new Date(`${note.date}T12:00:00`).toLocaleDateString([], {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
  return (
    <article className="py-4 first:pt-0 last:pb-0">
      <div className="flex items-center justify-between gap-2">
        <p className="kicker text-sage truncate">
          {sunday}
          {note.sermonTitle ? ` · ${note.sermonTitle}` : ''}
        </p>
        <span className="text-xs text-muted shrink-0">{note.authorName}</span>
      </div>

      {note.body.trim() && (
        <div className="mt-2">
          <Markdown content={note.body} />
        </div>
      )}

      <div className="flex items-center justify-between mt-3">
        <div className="flex items-center gap-3">
          <span className="text-xs text-muted">
            {note.visibility === 'private' ? '🔒 Private' : '👪 Family'}
          </span>
          {note.sermonUrl && (
            <a
              href={note.sermonUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-oxblood underline underline-offset-2"
              onClick={(e) => e.stopPropagation()}
            >
              🔗 Watch
            </a>
          )}
        </div>
        {note.mine && (
          <IconButton icon={<IconPencil />} label="Edit note" onClick={onEdit} />
        )}
      </div>
    </article>
  );
}

function PrayerSection({
  composerRef,
}: {
  composerRef: React.MutableRefObject<HTMLFormElement | null>;
}) {
  const qc = useQueryClient();
  const [tab, setTab] = useState<'active' | 'answered'>('active');
  const [draft, setDraft] = useState('');
  const [draftMentions, setDraftMentions] = useState<string[]>([]);

  const list = useQuery(prayersQueryOptions(tab));

  const create = useMutation({
    mutationFn: (args: { body: string; mentions: string[] }) =>
      api.post<{ prayer: Prayer }>('/prayers', args),
    onSuccess: () => {
      setDraft('');
      setDraftMentions([]);
      void qc.invalidateQueries({ queryKey: ['prayers'] });
    },
  });

  const answer = useMutation({
    mutationFn: (id: string) => api.post(`/prayers/${id}/answer`, {}),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['prayers'] }),
  });

  return (
    <Section title="Prayer" subtitle="What He's heard. What He's answered.">
      <div className="inline-flex rounded-full border border-line p-1 bg-paper">
        <Pill active={tab === 'active'} onClick={() => setTab('active')}>
          Active
        </Pill>
        <Pill active={tab === 'answered'} onClick={() => setTab('answered')}>
          Answered
        </Pill>
      </div>

      {tab === 'active' && (
        <form
          ref={composerRef}
          onSubmit={(e) => {
            e.preventDefault();
            if (draft.trim())
              create.mutate({ body: draft.trim(), mentions: draftMentions });
          }}
          className="mt-4 rounded-xl border border-line bg-paper p-3"
        >
          <MentionTextarea
            value={draft}
            onChange={(v, m) => {
              setDraft(v);
              setDraftMentions(m);
            }}
            rows={3}
            placeholder="What are you bringing to God?"
            className="w-full bg-transparent text-ink placeholder:text-muted resize-none focus:outline-none"
          />
          <div className="flex justify-end">
            <Button
              type="submit"
              size="sm"
              disabled={!draft.trim()}
              busy={create.isPending}
            >
              Post prayer
            </Button>
          </div>
        </form>
      )}

      <div className="mt-4 divide-y divide-line">
        {list.data?.prayers.map((p) => (
          <article key={p.id} className="py-4 first:pt-0 last:pb-0">
            <p className="text-ink leading-relaxed">{renderWithMentions(p.body)}</p>
            <div className="flex items-center justify-between mt-3">
              <span className="text-xs text-muted">
                {new Date(p.createdAt).toLocaleString([], {
                  month: 'short',
                  day: 'numeric',
                })}
              </span>
              {p.status === 'active' ? (
                <button
                  className="text-xs text-sage font-medium"
                  onClick={() => answer.mutate(p.id)}
                >
                  Mark answered ✓
                </button>
              ) : (
                <span className="text-xs text-sage italic">
                  Answered{' '}
                  {p.answeredAt
                    ? new Date(p.answeredAt).toLocaleDateString([], {
                        month: 'short',
                        day: 'numeric',
                      })
                    : ''}
                </span>
              )}
            </div>
            {p.answeredNote && (
              <p className="text-soft italic font-serif mt-2 border-l-2 border-sage pl-3">
                {p.answeredNote}
              </p>
            )}
          </article>
        ))}
        {list.data?.prayers.length === 0 && (
          <p className="text-muted text-center py-4 italic">
            {tab === 'active'
              ? 'Quiet today. No active requests.'
              : 'No answered prayers yet.'}
          </p>
        )}
      </div>
    </Section>
  );
}

/**
 * Go Deeper — the video catalog, grouped by theme tag. A video with
 * multiple tags appears under each. Tag-less videos collect in an
 * "Untagged" group so the owner can spot what still needs curation.
 *
 * Each card is a thumbnail + title that opens YouTube directly. We
 * deliberately do NOT embed iframes here — a dozen+ embeds would tank
 * scroll perf on phones and burn cellular data. The Today tab keeps a
 * single embedded devotional for the day's video; this surface is the
 * library, not the player.
 */
function GoDeeperSection() {
  const videos = useQuery(videosQueryOptions());
  // Which category's videos are popped out (by tag); null = closed.
  const [openTag, setOpenTag] = useState<string | null>(null);

  if (videos.isLoading) {
    return (
      <section>
        <h2 className="font-serif text-2xl text-ink">Go Deeper</h2>
        <p className="text-soft text-sm mt-2">Loading videos…</p>
      </section>
    );
  }
  if (!videos.data || videos.data.videos.length === 0) {
    return (
      <section>
        <h2 className="font-serif text-2xl text-ink">Go Deeper</h2>
        <p className="text-soft text-sm italic mt-2">
          No videos yet — the owner can add a YouTube playlist under Settings → Playlists.
        </p>
      </section>
    );
  }

  // Compact: show one tappable row per theme; the videos themselves open
  // in a pop-out sheet (mirrors the Grow "Practice" pop-out) so the
  // catalog stays short instead of unrolling every group inline.
  const categories = groupVideosByTag(videos.data.videos);
  const open = categories.find((c) => c.tag === openTag) ?? null;

  return (
    <section>
      <h2 className="mb-1 font-serif text-2xl text-ink">Go Deeper</h2>
      <p className="mb-4 font-serif italic text-soft">Devotionals by theme.</p>

      <ul className="card divide-y divide-line overflow-hidden">
        {categories.map((c) => (
          <li key={c.tag}>
            <button
              type="button"
              onClick={() => setOpenTag(c.tag)}
              className="flex w-full items-center gap-3 p-4 text-left transition-colors hover:bg-line/40"
            >
              <span className="min-w-0 flex-1 font-serif text-lg text-ink">
                {c.label}
              </span>
              <span className="shrink-0 text-xs text-muted">
                {c.videos.length} {c.videos.length === 1 ? 'video' : 'videos'}
              </span>
              <ChevronRight />
            </button>
          </li>
        ))}
      </ul>

      <Sheet open={!!open} onClose={() => setOpenTag(null)} title={open?.label}>
        <div className="px-5 pb-4">
          {open && <VideoPreviewList videos={open.videos} />}
        </div>
      </Sheet>
    </section>
  );
}

function ChevronRight() {
  return (
    <svg
      viewBox="0 0 24 24"
      className="w-4 h-4 shrink-0 text-muted"
      fill="none"
      aria-hidden
    >
      <path
        d="m9 6 6 6-6 6"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function VideoPreviewList({ videos }: { videos: VideoRow[] }) {
  const [first, ...rest] = videos;
  if (!first) return null;
  return (
    <div>
      <VideoCard video={first} />
      {rest.length > 0 && (
        <ul className="mt-1 divide-y divide-line">
          {rest.map((v) => (
            <CompactVideoRow key={v.videoId} video={v} />
          ))}
        </ul>
      )}
    </div>
  );
}

/** Title-only list row — opens YouTube in a new tab, like VideoCard,
 *  but without the thumbnail so the catalog stays compact. */
function CompactVideoRow({ video }: { video: VideoRow }) {
  return (
    <li>
      <a
        href={video.url}
        target="_blank"
        rel="noopener noreferrer"
        className="group flex items-center gap-2 py-2"
      >
        <span className="text-sage text-xs shrink-0" aria-hidden>
          ▶
        </span>
        <span className="flex-1 min-w-0 text-sm text-ink line-clamp-1 group-hover:text-oxblood">
          {video.title}
        </span>
        {video.audience === 'adult' && (
          <span className="text-[10px] uppercase tracking-wider font-medium text-oxblood border border-oxblood/40 rounded px-1.5 py-0.5 shrink-0">
            Adult
          </span>
        )}
        {video.audience === 'youth' && (
          <span className="text-[10px] uppercase tracking-wider font-medium text-sage border border-sage/40 rounded px-1.5 py-0.5 shrink-0">
            Youth
          </span>
        )}
      </a>
    </li>
  );
}

function VideoCard({ video }: { video: VideoRow }) {
  // YouTube exposes a stable hqdefault thumb at this URL — no extra
  // API call needed. The card opens YouTube in a new tab so the PWA
  // doesn't lose its scroll position.
  const thumb = `https://i.ytimg.com/vi/${encodeURIComponent(video.videoId)}/mqdefault.jpg`;
  return (
    <a
      href={video.url}
      target="_blank"
      rel="noopener noreferrer"
      className="card flex gap-3 p-2 items-center hover:bg-line/40"
    >
      <img
        src={thumb}
        alt=""
        width={120}
        height={68}
        loading="lazy"
        decoding="async"
        className="w-28 aspect-video object-cover rounded-md bg-line shrink-0"
      />
      <div className="flex-1 min-w-0">
        <h4 className="font-serif text-ink leading-snug line-clamp-2">{video.title}</h4>
        <div className="flex items-center gap-2 mt-1">
          {video.audience === 'adult' && (
            <span className="text-[10px] uppercase tracking-wider font-medium text-oxblood border border-oxblood/40 rounded px-1.5 py-0.5">
              Adult
            </span>
          )}
          {video.audience === 'youth' && (
            <span className="text-[10px] uppercase tracking-wider font-medium text-sage border border-sage/40 rounded px-1.5 py-0.5">
              Youth
            </span>
          )}
          {video.uploadDate && <p className="text-xs text-muted">{video.uploadDate}</p>}
        </div>
      </div>
    </a>
  );
}

function Pill({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={`px-5 h-9 rounded-full text-sm font-medium ${
        active ? 'bg-ink text-paper' : 'text-muted'
      }`}
    >
      {children}
    </button>
  );
}
